import React from "react";
import { Button, Card, CardContent, Typography } from "@mui/material";

export const detailsData = {
  "audit-planning": {
    title: "Audit Planning",
    steps: [
      {
        title: "Define Scope",
        content:
          "Identify the audit objectives and determine which areas or processes to cover. Clearly define the boundaries of the audit. This involves detailing the departments, time frames, and specific business processes that will be audited. The scope should align with the overall risk assessment and business priorities.",
        template:
          "Check the sample template and download the document",
        documentUrl:
          "/wpauditframework/Compliance-Risk-Management-Applying-the-COSO-ERM-Framework.pdf",
      },
      {
        title: "Risk Assessment",
        content:
          "Conduct a comprehensive risk assessment to prioritize high-risk areas. Evaluate potential risks based on likelihood and impact. Gather data from past audits, industry benchmarks, and internal control assessments. This analysis helps focus your audit on areas that could significantly impact the organization.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Develop Audit Plan",
        content:
          "Outline a detailed audit plan that includes your methodology, timeline, resources, and deliverables. Clearly articulate the objectives, audit procedures, and expected outcomes. Include milestones and checkpoints to track progress throughout the audit cycle, and allow flexibility to adjust for emerging risks.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Obtain Approval",
        content:
          "Review the completed audit plan with the audit committee or management to secure formal approval. This step ensures alignment with organizational goals and confirms the allocation of necessary resources. Present your plan, risk assessment, and methodology to demonstrate the audit’s robustness and readiness for execution.",
        template:
        "Check the sample template and download the document",
      },
    ],
  },
  "audit-fieldwork": {
    title: "Audit Fieldwork",
    steps: [
      {
        title: "Pre-Fieldwork Meeting",
        content:
          "Schedule a meeting with key stakeholders to communicate the audit objectives, scope, and timeline. Ensure all team members and participants understand their roles and responsibilities during the fieldwork phase.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Data Collection",
        content:
          "Gather necessary data through document reviews, interviews, observations, and data analysis. Use systematic methods to ensure that the information collected supports your audit findings effectively.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Testing Controls",
        content:
          "Test internal controls to verify their effectiveness. Develop test procedures, perform control testing, and document the results carefully to support your audit conclusions.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Document Findings",
        content:
          "Record all observations and evidence meticulously. Ensure your documentation is clear, precise, and provides a solid basis for your audit conclusions. This will be critical for later review and reporting.",
        template:
        "Check the sample template and download the document",
      },
    ],
  },
  "audit-reporting": {
    title: "Audit Reporting",
    steps: [
      {
        title: "Draft Report",
        content:
          "Prepare a draft report that summarizes audit findings, risks, and recommendations. The draft should be comprehensive, clear, and highlight key issues and suggested improvements.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Review and Feedback",
        content:
          "Circulate the draft report among key stakeholders to gather feedback. Use the feedback to refine and improve the report, ensuring accuracy and clarity in the final document.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Finalize Report",
        content:
          "Incorporate all feedback and finalize the audit report. Ensure that the final version clearly presents findings and recommendations, making it actionable for decision-makers.",
        template:
        "Check the sample template and download the document",
      },
      {
        title: "Distribute Report",
        content:
          "Distribute the finalized report to all relevant parties and ensure it is properly archived for future reference. This step is essential for accountability and ongoing improvement.",
        template:
        "Check the sample template and download the document",
      },
    ],
  },
};

export const DetailsSection = ({ categoryKey }) => {
  const categoryData = detailsData[categoryKey];
  if (!categoryData) return null;
  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {categoryData.title}
      </Typography>
      {categoryData.steps.map((step, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">{step.title}</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {step.content}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {step.template}
            </Typography>
            {step.documentUrl && (
              <Button
                variant="contained"
                color="primary"
                href={step.documentUrl}
                target="_blank"
              >
                Download Document
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
